/**
 * IL TEMA MaeDesign beta v1, in un file che non porta con sé l'applicazione.
 *
 * ⚠️ Stava dentro `AppProviders`, e da lì non si poteva **provare**: importarlo tirava dentro il
 * router, i devtools e i provider — cioè, per leggere un oggetto di configurazione, mezza app e
 * un tentativo di connessione a `localhost:3000` nello stderr delle prove. Il tema è dato, non
 * comportamento: sta da solo, e chi lo vuole controllare lo monta e basta.
 *
 * Il perché di ogni scelta sta accanto alla scelta. Le tre da non toccare per distrazione sono
 * il peso «medium» a 500, il pallino dello `Switch` spento e l'ordine della nota nei campi.
 */
import { createTheme, type MantineColorsTuple } from '@mantine/core';
import { tokenOptionsFilter } from '@/shared/lib/tokenOptionsFilter';

// index 6 == #540409 (primaryShade light/dark)
const brand: MantineColorsTuple = [
  '#fbe9ea', '#f2c9cc', '#e39aa0', '#d56b73', '#c8454f',
  '#a82230', '#540409', '#470408', '#380306', '#2a0205',
];
// index 4 == #85B3D7 (azzurro brand); index 6 == #5B89AE (azzurro-dark, link)
const lightBlue: MantineColorsTuple = [
  '#eff6fb', '#d9e9f4', '#bcd7ec', '#9dc5e3', '#85b3d7',
  '#6f9fc7', '#5b89ae', '#4a748f', '#3c5f76', '#2d4a5d',
];

/**
 * ⚠️ Esportato con un nome: `tests/unit/notaSottoIlCampo.test.tsx` monta dei campi **con questo
 * tema** per controllare che i suoi default arrivino davvero dentro `TextInput`, `Select` e
 * `Autocomplete`. Un default di tema che non arriva non dà errore: resta scritto e senza effetto.
 */
export const temaMae = createTheme({
  primaryColor: 'brand',
  primaryShade: { light: 6, dark: 6 },
  colors: { brand, lightBlue },
  defaultRadius: 'md',
  // DS radii: chip/button 12 · card 14–16 · hero 22
  radius: { xs: '6px', sm: '8px', md: '12px', lg: '16px', xl: '22px' },
  fontFamily: "'IBM Plex Sans', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  /*
   * ⚠️ ⚠️ **IL PAVIMENTO DEI 13px (MaeDesign v3 §4.2, `NUCLEO`, «non si deroga»).**
   *
   * Fino al 28-09-2026 questa chiave non c'era, e l'assenza non era neutra: senza `fontSizes`
   * valgono i default di Mantine, dove `xs` e' **12px**. Sono 1.233 `size="xs"` in 287 file —
   * 645 dei quali su un `<Text>`, cioe' scritte da leggere a 12px — e nessuno le aveva scelte:
   * le ha ereditate chi non ha dichiarato la scala. E' il modo piu' economico di violare una
   * regola, perche' non lo si scrive da nessuna parte.
   *
   * La salita a IBM Plex (24-09, `4bacac3a`) aveva dichiarato il pavimento fuori scopo, e il
   * suo messaggio lo diceva: «Fuori da questo passo restano il pavimento dei 13px». Questo e'
   * quel passo, per la parte che si chiude dal tema. Il resto — i tre gradini che la scala aveva
   * sotto il pavimento e le misure scritte a mano nei sorgenti — e' salito lo stesso giorno, e i
   * tre gradini sono usciti da `maedesign-tokens.css`: finche' esistevano, un testo a 10px *era*
   * un gradino, e la regola restava verde mentre l'applicazione la violava.
   *
   * ⚠️ **In `var(--fs-*)`, non nei px del documento.** Il frammento di §7 scrive `xs: '13px'`,
   * e qui sarebbe sbagliato due volte: una misura in px non scala con l'impostazione di sistema
   * (WCAG 1.4.4, ed e' la guardia «nessuna misura del testo e' scritta in pixel»), e una seconda
   * scala accanto a `--fs-*` diverge — e' lo stesso motivo per cui i gradini esistono. Cosi'
   * Mantine e il foglio leggono gli **stessi** tredici valori, e cambiarne uno lo cambia in
   * tutt'e due i posti.
   *
   * ⚠️ **Questo passo cambia un gradino solo.** `sm`, `md`, `lg` e `xl` sono scritti al valore che
   * gia' avevano: qui vengono **dichiarati**, non spostati. §3 vuole il corpo a 15 e `lg` a 16, ma
   * quella e' una riduzione che tocca il `body` e ogni `<Text>` senza misura, cioe' una cosa da
   * guardare a schermo — e da poter togliere da sola se non convince. Sta nel commit dopo, per
   * quello. Stessa ragione per i titoli, che sono la terza parte.
   *
   * ⚠️ **Due gradini scendono — 28-09-2026, ed e' il passo che si toglie da solo.** `md` era 16px
   * e va a 15 (§3: «corpo predefinito»), `lg` era 18 e va a 16. Tocca il `body` — Mantine ci mette
   * `--mantine-font-size-md` — piu' 1.077 `<Text>`/`<Anchor>` senza misura e 105 `size="lg"`. In
   * un passo che alza un pavimento fa uno strano effetto, ma e' la scala del documento:
   * l'alternativa era tenerci un corpo che nessuno aveva scelto solo perche' era piu' grande.
   *
   * ⚠️ Le interlinee arrivano con lui e non prima: §3 le lega al corpo (`md` 1.5 contro il 1.55
   * di Mantine, `xl` 1.3 contro 1.65). Separarle vorrebbe dire spedire un corpo nuovo con
   * l'interlinea del corpo vecchio, che e' la meta' peggiore delle due.
   */
  fontSizes: {
    xs: 'var(--fs-compact)',   /* 13px · il pavimento: §4.2 non lo fa scendere */
    sm: 'var(--fs-body)',      /* 14px · invariato rispetto al default Mantine */
    md: 'var(--fs-body-lg)',   /* 15px · corpo predefinito §3 (era 16) */
    lg: 'var(--fs-lead)',      /* 16px · (era 18) */
    xl: 'var(--fs-h3)',        /* 20px · invariato */
  },
  lineHeights: { xs: '1.4', sm: '1.45', md: '1.5', lg: '1.5', xl: '1.3' },
  /*
   * ⚠️ **Le sei misure, non le tre del documento.** §3 dichiara `h1` 28, `h2` 20, `h3` 16 e si
   * ferma li'. Fermarsi li' anche qui rovescerebbe la gerarchia: i default Mantine per `h4`
   * sono 18px, quindi con `h3` a 16 un titolo di quarto livello diventerebbe **piu' grande** del
   * terzo. Sono 20 `order={4}` e 15 `order={5}` nel repo, cioe' un difetto che si vede. Da `h4`
   * in giu' la discesa continua sui gradini che ci sono — 15, 14, 13 — e si ferma sul pavimento.
   *
   * ⚠️ La crenatura negativa che §3 chiede (`h1` -0.022em, `h2` -0.017em) non sta qui: il tema
   * Mantine non ha una chiave per la spaziatura dei titoli. La portano gia' i punti che disegnano
   * un titolo davvero — `MaePageHead` e `MaeDrawerTitle` in `shared/desktop/MaeShell.tsx` —, che
   * per la stessa ragione non passano da `<Title>` ma da `<Text>` con `--fs-h1`.
   *
   * ⚠️ Il peso resta **700** e non scende a 600 come chiede §3. Non e' una dimenticanza: i titoli
   * di pagina veri li scrive `MaePageHead` con `fontWeight: 700` in linea, quindi abbassare qui
   * renderebbe i 74 `<Title>` piu' leggeri delle intestazioni accanto a cui stanno — una
   * gerarchia peggiore di quella che c'e'. Si sposta quando si spostano insieme, guardando le
   * pagine; e' una decisione di grafica, non un effetto del pavimento.
   */
  headings: {
    fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
    fontWeight: '700',
    sizes: {
      h1: { fontSize: 'var(--fs-h1)', lineHeight: '1.18' },      /* 28px */
      h2: { fontSize: 'var(--fs-h3)', lineHeight: '1.3' },       /* 20px */
      h3: { fontSize: 'var(--fs-lead)', lineHeight: '1.35' },    /* 16px */
      h4: { fontSize: 'var(--fs-body-lg)', lineHeight: '1.4' },  /* 15px */
      h5: { fontSize: 'var(--fs-body)', lineHeight: '1.45' },    /* 14px */
      h6: { fontSize: 'var(--fs-compact)', lineHeight: '1.45' }, /* 13px · il pavimento */
    },
  },
  /*
   * ⚠️ `fontFamilyMonospace` c'e' **dal 24-09-2026**, e prima era assente di proposito: §2 voleva
   * una famiglia sola, e dichiararlo qui avrebbe dato una seconda faccia a ogni `<Code>` di
   * Mantine. v3 §4.1 la seconda famiglia la prevede — solo per codici, matricole e
   * identificativi — quindi la chiave torna, e punta alla faccia che spediamo davvero.
   *
   * ⚠️ Non e' la porta da cui si rende un codice: quella resta `shared/components/Codice.tsx`,
   * con `MW_CODE_STYLE` per un identificativo in linea e `MW_FONT_MACCHINA` per un blocco di
   * testo macchina. Questa chiave copre i punti che il mono lo prendono dal tema senza passare
   * di li', che e' il modo in cui quattro `<Code block>` erano sfuggiti al censimento del 04-09.
   */
  fontFamilyMonospace: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  // DS elevation (tokens.css --elev-*): soft, diffuse, mai dura.
  shadows: {
    xs: '0 1px 2px rgba(0,0,0,0.05)',
    sm: '0 4px 12px rgba(0,0,0,0.06)',
    md: '0 10px 26px rgba(0,0,0,0.10)',
    lg: '0 16px 38px -22px rgba(0,0,0,0.30)',
    xl: '0 24px 60px rgba(0,0,0,0.18)',
  },
  cursorType: 'pointer',
  /*
   * ⚠️ Il peso «medium» resta **500**. Mantine 9 lo porta a 600 e introduce `fontWeights` nel
   * tema; qui lo teniamo a 500 di proposito, non per pigrizia. Il codice ha 353 `fw={600}` e
   * 312 `fw={700}` scritti a mano, che **non** cambiano: lasciando salire il medium del tema,
   * quello che era «un gradino sopra» diventerebbe uguale e la gerarchia tipografica si
   * schiaccerebbe in tutta l'applicazione, senza che nessun test se ne accorga. Alzarlo a 600 e'
   * una decisione di grafica da prendere guardando le pagine, non un effetto dell'aggiornamento.
   */
  fontWeights: { regular: '400', medium: '500', bold: '700' },
  components: {
    Card: {
      defaultProps: { shadow: 'sm', withBorder: true, radius: 'lg' },
    },
    Paper: {
      defaultProps: { radius: 'lg' },
    },
    Button: {
      defaultProps: { radius: 'md' },
    },
    Badge: {
      defaultProps: { radius: 'sm', variant: 'light' },
    },
    /* Mantine 9 accende il pallino dentro il cursore dell'interruttore. Sono 161 `Switch` in 92
       file: il segno nuovo si adotta guardandoli, non arrivandoci addosso con l'aggiornamento. */
    Switch: {
      defaultProps: { withThumbIndicator: false },
    },
    /*
     * ⚠️ LA NOTA VA **SOTTO IL CAMPO**, non fra l'etichetta e il campo.
     * ================================================================
     *
     * Mantine mette la `description` al secondo posto, cioè fra l'etichetta e il riquadro. Dove
     * due campi stanno affiancati e uno solo ha la nota — «Paese» accanto a «Forma giuridica»,
     * «Valuta capitale» accanto a «Capitale sociale» — quel campo **scende**, e i due riquadri
     * non sono più alla stessa altezza: una riga di modulo che si disallinea da sé. Sotto, la
     * nota non sposta niente, e si legge dopo aver visto il campo, che è anche l'ordine in cui
     * serve: prima cosa mi stai chiedendo, poi come si scrive.
     *
     * ⚠️ Sta **qui** e non nei singoli campi. Sono 387 `description` in tutta l'applicazione, e
     * fino al 19-09-2026 l'ordine era corretto a mano in quattro punti di due file
     * (`working-hours/RotationsPanel`, `ShiftCalendarPanel`): una regola applicata dove qualcuno
     * se n'era accorto, cioè un'incoerenza in tutti gli altri 383 — e nessun modo di sapere
     * quali fossero voluti.
     *
     * L'errore resta per ultimo: è la cosa da leggere subito, e una nota lunga fra il riquadro e
     * il messaggio lo allontanerebbe proprio quando conta.
     */
    InputWrapper: {
      defaultProps: { inputWrapperOrder: ['label', 'input', 'description', 'error'] },
    },
    /* «andrea marcelli» deve trovare «Marcelli Andrea». */
    Select: { defaultProps: { filter: tokenOptionsFilter } },
    MultiSelect: { defaultProps: { filter: tokenOptionsFilter } },
    Autocomplete: { defaultProps: { filter: tokenOptionsFilter } },
    TagsInput: { defaultProps: { filter: tokenOptionsFilter } },
    /* Il formato di Mantine è «novembre 19, 2026»: all'americana anche in italiano. */
    DatePickerInput: {
      defaultProps: { valueFormat: 'DD/MM/YYYY' },
    },
  },
});
